<script setup>
// Conectar/desconectar caixas de entrada do agente, inline (sem modal).
// Reutiliza o store aiInboxes. Usado na aba "Lançamento".
import { computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  agent: { type: Object, required: true },
});
const emit = defineEmits(['update']);

const store = useStore();
const { t } = useI18n();

const inboxes = useMapGetter('aiInboxes/getInboxes');
const availableInboxes = useMapGetter('aiInboxes/getAvailableInboxes');
const inboxUiFlags = useMapGetter('aiInboxes/getUIFlags');

const isLoading = computed(() => inboxUiFlags.value.fetchingList);
const connectedCount = computed(() => inboxes.value?.length || 0);

const fetchData = () =>
  Promise.all([
    store.dispatch('aiInboxes/get', { agentId: props.agent.id }),
    store.dispatch('aiInboxes/getAvailable', { agentId: props.agent.id }),
  ]);

const getInboxIcon = channelType => {
  const icons = {
    'Channel::WebWidget': 'i-lucide-message-circle',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwilioSms': 'i-lucide-message-square',
    'Channel::Whatsapp': 'i-lucide-phone',
    'Channel::Api': 'i-lucide-code',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
    'Channel::Line': 'i-lucide-message-circle',
  };
  return icons[channelType] || 'i-lucide-inbox';
};

const getChannelLabel = channelType => {
  if (!channelType) return '';
  return channelType.split('::')[1] || channelType;
};

const handleConnect = async inbox => {
  try {
    await store.dispatch('aiInboxes/connect', {
      agentId: props.agent.id,
      inboxId: inbox.id,
    });
    useAlert(t('AI_AGENTS.INBOXES.MESSAGES.CONNECT_SUCCESS'));
    emit('update');
  } catch (error) {
    useAlert(error?.message || t('AI_AGENTS.INBOXES.MESSAGES.CONNECT_ERROR'));
  }
};

const handleDisconnect = async connection => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!window.confirm(t('AI_AGENTS.INBOXES.CONFIRM_DISCONNECT'))) return;
  try {
    await store.dispatch('aiInboxes/disconnect', {
      agentId: props.agent.id,
      connectionId: connection.id,
    });
    useAlert(t('AI_AGENTS.INBOXES.MESSAGES.DISCONNECT_SUCCESS'));
    emit('update');
  } catch (error) {
    useAlert(
      error?.message || t('AI_AGENTS.INBOXES.MESSAGES.DISCONNECT_ERROR')
    );
  }
};

onMounted(fetchData);
</script>

<template>
  <div>
    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <Spinner size="lg" />
    </div>

    <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Conectadas -->
      <div>
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t('AI_AGENTS.INBOXES.CONNECTED_INBOXES') }}
          </h4>
          <span
            class="px-2 py-0.5 text-xs font-medium bg-n-brand/10 text-n-brand rounded-full"
          >
            {{ connectedCount }}
          </span>
        </div>
        <div v-if="inboxes.length" class="space-y-2">
          <div
            v-for="connection in inboxes"
            :key="connection.id"
            class="flex items-center justify-between p-3 bg-n-alpha-1 border border-n-weak rounded-xl group"
          >
            <div class="flex items-center gap-3 min-w-0">
              <span
                :class="getInboxIcon(connection.inbox?.channel_type)"
                class="w-4 h-4 text-n-brand shrink-0"
              />
              <div class="min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ connection.inbox?.name }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ getChannelLabel(connection.inbox?.channel_type) }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="p-1.5 rounded-lg text-n-slate-11 hover:text-n-ruby-10 hover:bg-n-ruby-3 transition-colors"
              :title="t('AI_AGENTS.INBOXES.DISCONNECT')"
              @click="handleDisconnect(connection)"
            >
              <span class="i-lucide-unlink w-4 h-4" />
            </button>
          </div>
        </div>
        <p v-else class="text-xs text-n-slate-10 py-4">
          {{ t('AI_AGENTS.INBOXES.NO_CONNECTED') }}
        </p>
      </div>

      <!-- Disponíveis -->
      <div>
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t('AI_AGENTS.INBOXES.AVAILABLE_INBOXES') }}
          </h4>
          <span
            class="px-2 py-0.5 text-xs font-medium bg-n-alpha-2 text-n-slate-11 rounded-full"
          >
            {{ availableInboxes.length }}
          </span>
        </div>
        <div v-if="availableInboxes.length" class="space-y-2">
          <div
            v-for="inbox in availableInboxes"
            :key="inbox.id"
            class="flex items-center justify-between p-3 bg-n-solid-2 border border-n-weak rounded-xl"
          >
            <div class="flex items-center gap-3 min-w-0">
              <span
                :class="getInboxIcon(inbox.channel_type)"
                class="w-4 h-4 text-n-slate-11 shrink-0"
              />
              <div class="min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ inbox.name }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ getChannelLabel(inbox.channel_type) }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="px-2.5 py-1 rounded-lg text-xs font-medium text-n-brand hover:bg-n-brand/10 transition-colors"
              @click="handleConnect(inbox)"
            >
              {{ t('AI_AGENTS.INBOXES.CONNECT') }}
            </button>
          </div>
        </div>
        <p v-else class="text-xs text-n-slate-10 py-4">
          {{ t('AI_AGENTS.INBOXES.NO_AVAILABLE') }}
        </p>
      </div>
    </div>
  </div>
</template>
